'use client';

/**
 * Forum NotificationBell — the SIG Community in-app notification bell.
 *
 * Polls GET /api/v1/forum/notifications every 60s (only while the tab is
 * visible — the interval skips hidden tabs and a visibilitychange listener
 * refreshes immediately on return). Opening the dropdown marks the listed
 * notifications read; a footer action marks everything read.
 *
 * SELF-CONTAINED + HARMLESS: renders nothing while logged out (the API
 * returns 401 → the bell hides itself), and any fetch failure degrades to an
 * empty state. Safe to mount on public pages. IN-APP ONLY — these rows live in
 * forum_notifications; nothing here ever dispatches email/SMS/push.
 *
 * Visual idiom mirrors components/notifications/notifications-bell.tsx (the
 * alert-inbox bell) so the two bells feel like siblings.
 */

import { formatDistanceToNow } from 'date-fns';
import { AtSign, Award, Bell, CheckCircle2, Inbox, Reply } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Popover } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type ForumNotificationKind = 'reply' | 'mention' | 'accepted' | 'desk_answer' | 'badge';

/** API row (dates arrive as ISO strings through JSON). */
interface NotificationItem {
  id: string;
  kind: ForumNotificationKind;
  actor_name: string | null;
  actor_email: string | null;
  thread_slug: string | null;
  thread_title: string | null;
  read_at: string | null;
  created_at: string;
}

interface NotificationsResponse {
  notifications: NotificationItem[];
  unreadCount: number;
}

const KIND_ICON: Record<ForumNotificationKind, typeof Reply> = {
  reply: Reply,
  mention: AtSign,
  accepted: CheckCircle2,
  desk_answer: CheckCircle2,
  badge: Award,
};

const KIND_VERB: Record<ForumNotificationKind, string> = {
  reply: 'replied to',
  mention: 'mentioned you in',
  accepted: 'accepted your answer on',
  desk_answer: 'posted a desk answer on',
  badge: 'You earned a new badge',
};

function actorLabel(n: NotificationItem): string {
  if (n.actor_name) return n.actor_name;
  const local = n.actor_email?.split('@')[0];
  return local || 'A member';
}

function relTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '';
  }
}

const POLL_MS = 60_000;

export function NotificationBell({ className }: { className?: string }) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const signedOutRef = useRef(false);

  const load = useCallback(async (): Promise<NotificationItem[]> => {
    if (signedOutRef.current) return [];
    setLoading(true);
    try {
      const res = await fetch('/api/v1/forum/notifications?limit=30', { cache: 'no-store' });
      if (res.status === 401) {
        // Logged out — hide the bell entirely and stop polling.
        signedOutRef.current = true;
        setSignedOut(true);
        return [];
      }
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as NotificationsResponse;
      const fresh = Array.isArray(data.notifications) ? data.notifications : [];
      setItems(fresh);
      setUnread(typeof data.unreadCount === 'number' ? data.unreadCount : 0);
      return fresh;
    } catch {
      // Degrade quietly — keep whatever we had.
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll every 60s, but ONLY while the tab is visible; refresh immediately
  // when the user returns to the tab.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') void load();
    };
    tick();
    const interval = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const markRead = useCallback(async (ids?: string[]) => {
    try {
      const res = await fetch('/api/v1/forum/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids && ids.length > 0 ? { ids } : { all: true }),
      });
      if (!res.ok) return;
      const now = new Date().toISOString();
      setItems((prev) =>
        prev.map((n) => (!n.read_at && (!ids || ids.includes(n.id)) ? { ...n, read_at: now } : n)),
      );
      if (ids && ids.length > 0) {
        setUnread((u) => Math.max(0, u - ids.length));
      } else {
        setUnread(0);
      }
    } catch {
      // Best-effort — the next poll reconciles.
    }
  }, []);

  // Opening the dropdown marks the LISTED notifications read (the badge zeroes
  // for what the member has now seen; "Mark all read" covers any older ones).
  const handleToggle = useCallback(() => {
    setOpen((wasOpen) => {
      if (!wasOpen) {
        void load().then((fresh) => {
          const unreadIds = fresh.filter((n) => !n.read_at).map((n) => n.id);
          if (unreadIds.length > 0) void markRead(unreadIds);
        });
      }
      return !wasOpen;
    });
  }, [load, markRead]);

  if (signedOut) return null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={handleToggle}
        aria-label="Community notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Community notifications"
        className={cn(
          'relative inline-flex h-8 w-8 items-center justify-center rounded-[3px] border border-[var(--color-hair-2)] text-[var(--color-text-muted)] transition-colors motion-reduce:transition-none hover:border-[var(--color-steel)] hover:text-[var(--color-steel)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-steel)]',
          className,
        )}
      >
        <Bell size={14} aria-hidden="true" />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 inline-flex min-w-[15px] items-center justify-center rounded-full border border-[var(--color-surface)] bg-[var(--color-steel)] px-[3px] text-[9px] font-medium leading-[14px] text-[var(--color-canvas)]"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
        {unread > 0 && <span className="sr-only">{unread} unread community notifications</span>}
      </button>

      <Popover
        open={open}
        anchor={anchorRef}
        onClose={() => setOpen(false)}
        align="end"
        className="w-[340px] max-w-[min(340px,calc(100vw-16px))] p-0"
      >
        <div className="flex items-center justify-between border-b border-[var(--color-hair)] px-3 py-2">
          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--color-faint)]">
            Community
          </span>
          {unread > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-steel)]">
              {unread} new
            </span>
          )}
        </div>

        <div className="max-h-[360px] overflow-y-auto">
          {loading && items.length === 0 ? (
            <p className="px-3 py-6 text-center text-[12px] text-[var(--color-text-muted)]">
              Loading…
            </p>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-3 py-8 text-center">
              <Inbox size={20} aria-hidden="true" className="text-[var(--color-faint)]" />
              <p className="text-[12px] text-[var(--color-text-muted)]">You’re all caught up</p>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--color-hair)]">
              {items.slice(0, 10).map((n) => {
                const Icon = KIND_ICON[n.kind] ?? Bell;
                const isUnread = !n.read_at;
                const href =
                  n.kind === 'badge'
                    ? '/community/leaderboard'
                    : n.thread_slug
                      ? `/community/t/${n.thread_slug}`
                      : '/community';
                const time = relTime(n.created_at);
                return (
                  <li key={n.id}>
                    <Link
                      href={href as never}
                      onClick={() => setOpen(false)}
                      className="flex items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-[var(--color-surface-2)] focus-visible:bg-[var(--color-surface-2)] focus-visible:outline-none"
                    >
                      <span
                        className={cn(
                          'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[3px] border',
                          isUnread
                            ? 'border-[color-mix(in_srgb,var(--color-steel)_36%,transparent)] text-[var(--color-steel)]'
                            : 'border-[var(--color-hair-2)] text-[var(--color-text-muted)]',
                        )}
                      >
                        <Icon size={13} aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          {isUnread && (
                            <span
                              aria-hidden="true"
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-steel)]"
                            />
                          )}
                          <span className="truncate text-[13px] font-medium text-[var(--color-text)]">
                            {n.kind === 'badge' ? KIND_VERB.badge : actorLabel(n)}
                          </span>
                        </span>
                        {n.kind !== 'badge' && (
                          <span className="mt-0.5 line-clamp-2 block text-[12px] text-[var(--color-text-muted)]">
                            {KIND_VERB[n.kind]}{' '}
                            <span className="text-[var(--color-text)]">
                              {n.thread_title ?? 'a thread'}
                            </span>
                          </span>
                        )}
                        {time && (
                          <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--color-faint)]">
                            {time}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-[var(--color-hair)]">
          <button
            type="button"
            onClick={() => void markRead()}
            className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-steel)] focus-visible:text-[var(--color-steel)] focus-visible:outline-none"
          >
            Mark all read
          </button>
          <Link
            href={'/community' as never}
            onClick={() => setOpen(false)}
            className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-steel)] focus-visible:text-[var(--color-steel)] focus-visible:outline-none"
          >
            Community
          </Link>
        </div>
      </Popover>
    </>
  );
}
